import React, {useContext, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";

import {deleteGroup, fetchGroupDetails, saveGroup} from "../../../../state/api/groupAPI";
import Input from "../../../../../../common/src/components/widget/Input";
import Button from "../../../../../../common/src/components/widget/Button";

import * as yup from "yup";
import FormContainer from "common/src/components/form/FormContainer";
import {useForm} from "common/src/hooks/form/formHook";
import Checkbox from "common/src/components/widget/Checkbox";
import ServiceAccountsForGroupList from "../service_accounts/ServiceAccountsForGroupList";
import {addErrorNotification, addSuccessNotification} from "../../../../state/notification/notifications";
import Fragment from "common/src/components/widget/Fragment";
import {useModal} from "../../../../context/ModalProvider";
import ConfirmationDialog from "common/src/components/widget/ConfirmationDialog";
import {hasRole} from "../../../../utils/userUtils";
import {Role} from "../../../../auth/roles";
import {UserContext} from "../../../../context/UserContext";

function GroupForm() {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {user, _} = useContext(UserContext);
    const {openModal, closeModal} = useModal();
    const {groupId} = useParams();
    const edit = typeof groupId !== "undefined";

    const groupDetails = useSelector(state => state.groupAPI[groupId]);

    const [isLoading, setIsLoading] = useState(false);
    const dispatch = useDispatch();

    const schema = {
        name: yup.string().max(255, t('length_exceeded_error')).required(),
        permissions: yup.object().required()
    };

    useEffect(() => {
        !!groupId && setIsLoading(true);
        !!groupId && dispatch(fetchGroupDetails({groupId}));
    }, [groupId]);

    useEffect(() => {
        if (groupDetails) {
            setValues(groupDetails);
            setFormNotDirty();
            if (isLoading) {
                setIsLoading(false);
            }
        }
    }, [groupDetails]);

    const {
        values,
        getField,
        setFormNotDirty,
        handleSubmit,
        setValues,
        isEdited
    } = useForm(submitGroup, {name: "", permissions: {}}, schema);
    const handleDeleteGroup = () => {
        if (!hasRole(user, Role.ADMIN_GROUPS)) {
            dispatch(addErrorNotification(
                {title: t(`toast.error`), content: t(`toast.insufficient_permissions`)})
            );
            return;
        }
        const handleDelete = () => {

            setIsLoading(true);
            return dispatch(deleteGroup(values.id)).unwrap()
                .then(
                    (_) => {
                        dispatch(addSuccessNotification(
                            {title: t('toast.success'), content: t('toast.success_delete_group')})
                        );
                        closeModal();
                        navigate("/groups");
                    }
                ).catch(
                    (error) => {
                        dispatch(addErrorNotification(
                            {title: t('toast.error'), content: t('toast.error_delete_group')})
                        );
                    }
                ).finally(
                    () => {
                        setIsLoading(false);
                    }
                );
        }
        openModal(
            {
                body: <ConfirmationDialog
                    onConfirm={handleDelete}
                    onCancel={closeModal}
                    confirmButtonText={t("dialog.yes")}
                    cancelButtonText={t("dialog.no")}
                    message={"Are you sure you want to delete this group?"}
                />,
                title: t("dialog.confirm"),
                backdropClose: true
            }
        );
    }
    async function submitGroup() {
        if (!hasRole(user, Role.ADMIN_GROUPS)) {
            dispatch(addErrorNotification(
                {title: t(`toast.error`), content: t(`toast.insufficient_permissions`)})
            );
            return;
        }
        setIsLoading(true);
        const requestData = {
            id: values.id,
            name: values.name,
            description: values.description,
            permissions: values.permissions
        };

        const successMessage = t(`toast.success_${edit ? 'edit' : 'add'}_group`);
        return await dispatch(saveGroup(requestData)).unwrap()
            .then(
                (_) => {
                    setFormNotDirty();
                    dispatch(addSuccessNotification(
                        {title: t('toast.success'), content: successMessage})
                    );
                    navigate("/groups")
                }
            ).catch(
                (error) => {
                    dispatch(addErrorNotification(
                        {title: t(`toast.error`), content: t(`toast.error_${edit ? 'edit' : 'add'}_group`)})
                    );
                }
            ).finally(
                () => {
                    setIsLoading(false);
                }
            );
    }

    return (
        <>
            <FormContainer onSubmit={handleSubmit} isEdited={isEdited}>
                <div className="card">
                    <header className="card-header">
                        <p className="card-header-title">
                            <span className="icon fz fz-users medium mr-1"/>
                            <span>{t('group.group')}</span>
                        </p>
                    </header>
                    <div className="card-content">
                        <div className="columns is-multiline">
                            <div className="column ">
                                <Input label={t('group.name')} formField={getField("name")} disabled={isLoading}/>
                            </div>
                        </div>
                        <div className="columns is-multiline">

                            <div className="column">
                                <Input label={t('group.description')} formField={getField("description")}
                                       disabled={isLoading}/>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex-grow-1 card">
                    <header className="card-header">
                        <p className="card-header-title">
                            <span className="icon fz fz-lock medium mr-1"/>
                            <span>{t('group.manage.permissions')}</span>
                        </p>
                    </header>
                    <div className="card-content table-card has-overflow-y-auto">
                        <>
                            <table className={"table is-fullwidth is-striped table-bordered is-bordered "}>
                                <thead className={"is-10"}>
                                <tr>
                                    <th className={"has-text-centered"} rowSpan={2}><span>{t('Public')}</span></th>
                                    <th className={"has-text-centered"} colSpan={2}><span
                                        className={"is-center is-aligned-center is-centered"}>{t('permissions.admin')}</span>
                                    </th>
                                    <th className={"has-text-centered"} colSpan={3}>
                                        <span>{t('permissions.dataset')}</span>
                                    </th>
                                    <th className={"has-text-centered"} rowSpan={2}>
                                        <span>{t('permissions.audit')}</span>
                                    </th>
                                    <th className={"has-text-centered"} rowSpan={2}><span>{t('permissions.API')}</span>
                                    </th>
                                    <th className={"has-text-centered"}><span>{t('permissions.dataLake')}</span>
                                    </th>
                                </tr>
                                <tr>
                                    <th className={"has-text-centered"}><span>{t('permissions.groups')}</span></th>
                                    <th className={"has-text-centered"}><span>{t('permissions.users')}</span></th>
                                    <th className={"has-text-centered"}><span>{t('permissions.create')}</span></th>
                                    <th className={"has-text-centered"}><span>{t('permissions.publish')}</span></th>
                                    <th className={"has-text-centered"}><span>{t('permissions.delete')}</span></th>
                                    <th className={"has-text-centered"}><span>{t('permissions.access')}</span></th>
                                </tr>
                                </thead>
                                <tbody className="table-content">
                                <tr>
                                    <td className={"has-text-centered"} data-column-name={t('Public')}>

                                        <Checkbox
                                            checked={true}
                                            disabled={true}
                                        />
                                    </td>
                                    <td className={"has-text-centered"} data-column-name={t('hasAdminGroups')}>

                                        <Checkbox
                                            disabled={isLoading}
                                            formField={getField("permissions.hasAdminGroups")}
                                        />
                                    </td>
                                    <td className={"has-text-centered"} data-column-name={t('hasAdminUsers')}>

                                        <Checkbox
                                            disabled={isLoading}
                                            formField={getField("permissions.hasAdminUsers")}
                                        />
                                    </td>
                                    <td className={"has-text-centered"} data-column-name={t('hasDatasetCreate')}>

                                        <Checkbox
                                            disabled={isLoading}

                                            formField={getField("permissions.hasDatasetCreate")}
                                        />
                                    </td>
                                    <td className={"has-text-centered"} data-column-name={t('hasDatasetPublish')}>

                                        <Checkbox
                                            disabled={isLoading}

                                            formField={getField("permissions.hasDatasetPublish")}
                                        />
                                    </td>
                                    <td className={"has-text-centered"} data-column-name={t('hasDatasetDelete')}>

                                        <Checkbox
                                            disabled={isLoading}

                                            formField={getField("permissions.hasDatasetDelete")}
                                        />
                                    </td>

                                    <td className={"has-text-centered"} data-column-name={t('hasAuditRead')}>
                                        <Checkbox
                                            disabled={isLoading}

                                            formField={getField("permissions.hasAuditRead")}
                                        />
                                    </td>
                                    <td className={"has-text-centered"} data-column-name={t('API')}>

                                        <Checkbox
                                            checked={true}
                                            disabled={true}
                                        />
                                    </td>
                                    <td className={"has-text-centered"}
                                        data-column-name={t('permissions.hasDataLakeAccess')}>

                                        <Checkbox
                                            disabled={isLoading}
                                            formField={getField("permissions.hasDataLakeAccess")}
                                        />
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </>
                    </div>
                </div>

                <div className="card-footer-buttons">
                    <Button isSubmit={true} color="primary" label={edit ? t('save') : t('submit')}
                            isLoading={isLoading}/>
                    <Button color="primary" isOutlined={true} label={t('cancel')}
                            onClick={() => navigate("/groups")}/>
                    <Fragment rendered={(groupId !== undefined && values?.id)}>
                        <Button onClick={() => handleDeleteGroup()} color="danger"
                                label={t('delete')} className={"is-outlined"}/>
                    </Fragment>
                </div>
            </FormContainer>

            {groupDetails && groupDetails.id && !isLoading &&
                <>
                    <ServiceAccountsForGroupList group={groupDetails}/>
                </>
            }

        </>
    )
        ;
}

export default GroupForm;
